import { takeLatest, put, call, all, fork, select } from 'redux-saga/effects';
import {
  VALIDATE_COUPON_REQUEST,
  PROCESS_STRIPE_PAYMENT_REQUEST,
} from '../../constants/ActionTypes';
import {
  validateCouponSuccess,
  validateCouponFailure,
  updateCartTotals,
  processStripePaymentSuccess,
  processStripePaymentFailure,
} from '../actions/CartActions';
import { toast } from 'react-toastify';

// Stripe payment API call
const processStripePaymentAPI = async (paymentData) => {

  try {
    const { stripe, elements, amount, currency = 'usd', billingDetails, formValues, session_id, coupon_code } = paymentData;

    if (!stripe || !elements) {
      throw new Error('Stripe has not been initialized');
    }

    // Create payment method
    const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement('cardNumber'),
      billing_details: billingDetails
    });

    if (paymentMethodError) {
      throw paymentMethodError;
    }

    // Format form data for the API
    const formData = new FormData();
    const payloadData = {
      amount: amount,
      currency: currency,
      payment_method_id: paymentMethod.id,
      credit_card_no: paymentMethod.card ? `**** **** **** ${paymentMethod.card.last4}` : "",
      name_on_card: billingDetails.name,
      address: billingDetails.address.line1,
      first_name: formValues.firstName,
      middle_name: formValues.middleName,
      last_name: formValues.lastName,
      email: formValues.email,
      phone: formValues.phone,
      city: formValues.city,
      state: formValues.state,
      zipcode: formValues.zipCode,
      driving_license_number: formValues.dlNumber,
      dl_state: formValues.dlState,
      dob: formValues.dob,
      password: formValues.password,
      military_affiliation: formValues.affiliated,
      paymentMethod: formValues.paymentMethod,
      termAndCond: formValues.termAndCond,
      proceed: formValues.proceed,
      session_id: session_id,
      coupon_code: coupon_code || '',
      ...(!!formValues.section_data && { section_data: formValues.section_data })
    };

    formData.append("sentFormData", JSON.stringify(payloadData));

    // Make API call to your backend
    const response = await fetch(`${process.env.REACT_APP_SERVER_URL_FOR_CDL}/ws_process_stripe_payment`, {
      method: "POST",
      body: formData,
    });

    const data = await response.json();
    if (data.status !== 200) {
      throw new Error(data.message || "Payment failed");
    }

    return data;
  } catch (err) {
    const errorMessage = err.message || 'An error occurred while processing your request';
    toast.error(errorMessage);
  }


};

// Coupon Saga
function* validateCouponSaga(action) {
  try {
    const cartTotal = yield select(state => state.cart.total);
    const cartItems = yield select(state => [
      ...state.wizardLaunch.endorsements || [],
      ...state.wizardLaunch.moreClassSelection || [],
      ...state.wizardLaunch.directCourses || []
    ]);

    // Format section data from cart items
    const sectionData = cartItems
      .filter(item => item.section_id && item.courseType !== "Instructor-Led")
      .map(item => ({
        section_id: parseInt(item.section_id),
        price: item.price
      }));


    // Format form data for the API
    const formData = new FormData();
    const payloadData = {
      coupon_code: action.payload,
      total_price: cartTotal,
      section_data: sectionData
    };

    formData.append("sentFormData", JSON.stringify(payloadData));

    const response = yield call(fetch, `${process.env.REACT_APP_SERVER_URL_FOR_CDL}/ws_verify_coupon_code`, {
      method: 'POST',
      body: formData,
    });

    const data = yield response.json();

    if (response.ok) {
      if(data.status==='error'){
        yield put(validateCouponFailure(data.message || 'Coupon is not valid'))
      }else{
        // Map the API response data to match our state structure
      const couponData = {
        code: action.payload,
        discount: Math.abs(data.discountedTotalPrice) || 0,
        type: data.discountType,
        ...data
      };
      yield put(validateCouponSuccess(couponData));
      yield put(updateCartTotals());
      }
    } else {
      yield put(validateCouponFailure(data.message || 'Invalid coupon code'));
    }
  } catch (error) {
    yield put(validateCouponFailure('Failed to validate coupon. Please try again.'));
  }
}


// Stripe Payment Saga
function* handleStripePayment(action) {
  try {
    const response = yield call(processStripePaymentAPI, action.payload);

     // Get the queryParams from redux state
     const queryParams = yield select(state => state.wizardLaunch.queryParams);
      if(queryParams && (response.status === 200 || response.status === 201)){
        // Extract UTM parameters from queryParams
        const utmParams = {};
        Object.keys(queryParams).forEach(key => {
          if (key.startsWith('utm_')) {
            utmParams[key] = queryParams[key];
          }
        });
        
        // Only proceed with HubSpot if UTM parameters exist
        if (Object.keys(utmParams).length > 0) {
        // Format UTM parameters as a query string
        const utmString = Object.entries(utmParams).map(([key, value]) => `${key}=${value}`).join('&');
        
        const hubspotDataFormData = new FormData();
        const hubspotData = {
          firstname:  action.payload.formValues.firstName,
          lastname: action.payload.formValues.lastName,
          email: action.payload.formValues.email,
          state: action.payload.formValues.state,
          zip: action.payload.formValues.zipCode,
          phone: action.payload.formValues.phone,
          affiliated_with_us_military: action.payload.formValues.affiliated,
          lead_type: 'ELDT | Signup',
          utm: utmString
        };
    
        hubspotDataFormData.append("sentFormData", JSON.stringify(hubspotData));
    
        const hunspotresponse = yield call(fetch, `${process.env.REACT_APP_SERVER_URL_FOR_CDL}/ws_hubspot_contact`, {
          method: 'POST',
          body: hubspotDataFormData,
        });
      }
      }
     

    yield put(processStripePaymentSuccess(response));
  } catch (error) {
    yield put(processStripePaymentFailure(error.message));
  }
}

// Watcher Sagas
function* watchCouponSaga() {
  yield takeLatest(VALIDATE_COUPON_REQUEST, validateCouponSaga);
}

function* watchStripePaymentSaga() {
  yield takeLatest(PROCESS_STRIPE_PAYMENT_REQUEST, handleStripePayment);
}


// Root Saga
export default function* rootSaga() {
  yield all([
    fork(watchCouponSaga),
    fork(watchStripePaymentSaga),
  ]);
}
